/**
 * What the "Request for shift booking" popup shows for one day and shift.
 *
 * Employees request a shift, not a particular cell, so the popup gathers every
 * pending pickup request on the shift's cells and every posted, unfilled half
 * they can be placed into, split by role type (nurses and CNAs). A request with
 * no such half left for it is not shown: once the shift's open positions are
 * filled its leftover requests drop out, without telling those employees.
 */

const HALVES = ['both_halves', 'first_half', 'second_half'];
const ROLE_TYPES = ['nurse', 'cna'];

export const HALF_LABELS = { first_half: '1st half', second_half: '2nd half' };

// A slot only counts as available when it has been posted (open for pickup)
// and is not already filled.
export const isHalfAvailable = (cell, half) => {
  if (half === 'first_half') {
    return cell.is_first_half_posted && !cell.first_half_user && !cell.both_halves_user;
  }
  if (half === 'second_half') {
    return cell.is_second_half_posted && !cell.second_half_user && !cell.both_halves_user;
  }
  return cell.is_both_halves_posted && !cell.first_half_user && !cell.second_half_user && !cell.both_halves_user;
};

/**
 * Groups one day and shift of a scheduling period by role type.
 *
 * @param {object} periodData - The period as the scheduling page holds it (`units[].roles[].cells[]`).
 * @param {string} day - The shift's date, 'YYYY-MM-DD'.
 * @param {number} shiftOrder - 1, 2 or 3.
 * @returns {Array<object>} - `{ role, requests, slots }` per role type with at least one request, nurses first.
 *   `requests` are the ones a slot is left for (same half), oldest first; `slots` are
 *   `{ key, cell, half, unitName, rowNumber }` in schedule order.
 */
export const getShiftRequestGroups = (periodData, day, shiftOrder) => {
  const groups = Object.fromEntries(ROLE_TYPES.map((role) => [role, { role, requests: [], slots: [] }]));

  (periodData?.units || []).forEach((unit) => {
    // The schedule numbers a unit's rows per role type, in the order the unit
    // lists them: rowNumber 2 is the row shown as "CNA-2" (SchedulingRoleCols).
    // The default rows, stored as "<label>-1", always come first.
    const rowCounts = {};

    unit.roles.forEach((role) => {
      const group = groups[role.role];
      if (!group) return;

      rowCounts[role.role] = (rowCounts[role.role] || 0) + 1;
      const rowNumber = rowCounts[role.role];

      role.cells.forEach((cell) => {
        if (cell.day !== day || cell.shift_order !== shiftOrder) return;

        group.requests.push(...(cell.assignment_requests || []));
        HALVES.forEach((half) => {
          if (isHalfAvailable(cell, half)) {
            group.slots.push({ key: `${cell.id}-${half}`, cell, half, unitName: unit.unit?.name || '', rowNumber });
          }
        });
      });
    });
  });

  return ROLE_TYPES.map((role) => groups[role])
    .map((group) => ({
      ...group,
      requests: group.requests.filter((request) => group.slots.some((slot) => slot.half === request.type)).sort((a, b) => a.id - b.id),
    }))
    .filter((group) => group.requests.length > 0);
};

/**
 * Counts the pending pickup requests of every day and shift, the ones the popup lists.
 *
 * @param {object} periodData - The period as the scheduling page holds it (`units[].roles[].cells[]`).
 * @returns {object} - `{ [day]: { [shiftOrder]: count } }`, only for shifts with requests a slot is left for.
 */
export const countShiftRequests = (periodData) => {
  const shiftsWithRequests = {};

  (periodData?.units || []).forEach((unit) => {
    unit.roles.forEach((role) => {
      role.cells.forEach((cell) => {
        if (!cell.assignment_requests?.length) return;

        shiftsWithRequests[cell.day] = shiftsWithRequests[cell.day] || new Set();
        shiftsWithRequests[cell.day].add(cell.shift_order);
      });
    });
  });

  const counts = {};
  Object.entries(shiftsWithRequests).forEach(([day, shiftOrders]) => {
    shiftOrders.forEach((shiftOrder) => {
      const count = getShiftRequestGroups(periodData, day, shiftOrder).reduce((sum, group) => sum + group.requests.length, 0);
      if (!count) return;

      counts[day] = counts[day] || {};
      counts[day][shiftOrder] = count;
    });
  });

  return counts;
};

// Two placements clash when they cover the same time: either is the whole shift, or both are the same half.
const halvesOverlap = (a, b) => a === 'both_halves' || b === 'both_halves' || a === b;

/**
 * Tells whether a request can go into a slot given what is already placed.
 *
 * @param {object} request - The pickup request (`id`, `type`, `user_id`).
 * @param {object} slot - A slot from `getShiftRequestGroups`.
 * @param {Array<{request: object, slot: object}>} placements - The current placements.
 * @returns {boolean} - True when the slot is the half the employee asked for, is still empty, does not
 *   clash with another placement in the same cell, and does not book the employee twice at the same time.
 */
export const canPlaceRequest = (request, slot, placements) =>
  request.type === slot.half &&
  !placements.some(
    (placement) =>
      placement.request.id !== request.id &&
      ((placement.slot.cell.id === slot.cell.id && halvesOverlap(placement.slot.half, slot.half)) ||
        (placement.request.user_id === request.user_id && halvesOverlap(placement.slot.half, slot.half))),
  );
